import { useEffect, useRef, useState } from 'react'

/**
 * Custom React hook that debounces a value or function call.
 * Delays updating the debounced value until after the specified delay has passed
 * since the last change to the input value.
 *
 * @param {any|function} value - The value or function to debounce. If a function, it will be called after the delay.
 * @param {number} [delay=600] - The delay in milliseconds before updating the debounced value.
 * @returns {any} The debounced value. For functions, returns undefined as the function is executed instead.
 */
export default function useDebounce(value, delay = 400) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value)
  const callbackRef = useRef(null)

  if (typeof value === 'function') {
    callbackRef.current = value
  }

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        if (typeof value === 'function') {
          callbackRef.current()
        } else {
          setDebouncedValue(value)
        }
      }, delay)

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler)
      }
    },
    [value, delay], // Only re-call effect if value or delay changes
  )

  return debouncedValue
}
